import { createFileRoute } from '@tanstack/react-router';

import { JobForm } from '@/components/organisms/jobs/JobForm';

type JobBookingType = 'ONE_OFF' | 'RECURRING';
type NewJobSearch = {
  contactId?: string;
  locationId?: string;
  jobTypeId?: string;
  /** Legacy deep links chose the booking type before opening the form. */
  type?: JobBookingType;
  estimateId?: string;
  /** SPEC-projects — attach the new job to this project. */
  projectId?: string;
  /** The Telnyx call this job is being created from — recorded on the job. */
  callControlId?: string;
  /** The browser's own id for that call leg — opaque, used to find the call. */
  callId?: string;
};

export const Route = createFileRoute('/_dashboard/dashboard/jobs/new')({
  validateSearch: (search: Record<string, unknown>): NewJobSearch => {
    const next: NewJobSearch = {};
    if (typeof search.contactId === 'string') next.contactId = search.contactId;
    if (typeof search.locationId === 'string') next.locationId = search.locationId;
    if (typeof search.jobTypeId === 'string') next.jobTypeId = search.jobTypeId;
    if (typeof search.estimateId === 'string') next.estimateId = search.estimateId;
    if (typeof search.callControlId === 'string') next.callControlId = search.callControlId;
    if (typeof search.callId === 'string') next.callId = search.callId;
    if (typeof search.projectId === 'string') next.projectId = search.projectId;
    if (search.type === 'ONE_OFF' || search.type === 'RECURRING') {
      next.type = search.type;
    }
    return next;
  },
  component: NewJobPage,
});

/**
 * "New job" opens the form itself. There is no longer an intermediate picker
 * asking how much the Job Type should handle: the Job Type is chosen INSIDE the
 * form, and choosing it narrows the Business Unit options to that type's units
 * (see JobForm). One screen, one order — job type, then business unit.
 */
function NewJobPage() {
  const { contactId, locationId, jobTypeId, type, estimateId, projectId, callControlId, callId } =
    Route.useSearch();

  if (estimateId) {
    return <JobForm mode="create" initialEstimateId={estimateId} initialProjectId={projectId} />;
  }

  return (
    <JobForm
      mode="create"
      initialContactId={contactId}
      initialLocationId={locationId}
      initialJobTypeId={jobTypeId}
      initialBookingType={type}
      initialProjectId={projectId}
      sourceCallControlId={callControlId}
      sourceCallId={callId}
    />
  );
}
